import type * as React from "react";

export interface ConfidenceMeterProps {
  /** Confidence as 0–1 or 0–100. @default 0 */
  value?: number;
  /** Number of gauge dots. @default 5 */
  segments?: number;
  /** Show the trailing mono percentage. @default true */
  showLabel?: boolean;
  /** @default "md" */
  size?: "sm" | "md";
  style?: React.CSSProperties;
}

/**
 * Salesko ConfidenceMeter — a small segmented dot gauge for an extraction/match
 * confidence (0–1 or 0–100). High reads green, medium gold, low warm stone — never red
 * (low confidence is not an error). Optional mono % label.
 */
export function ConfidenceMeter({
  value = 0,
  segments = 5,
  showLabel = true,
  size = "md",
  style,
}: ConfidenceMeterProps) {
  const pct = value <= 1 ? value : value / 100;
  const filled = Math.max(0, Math.min(segments, Math.round(pct * segments)));
  const tone = pct >= 0.75 ? "var(--green-500)" : pct >= 0.4 ? "var(--gold-500)" : "var(--warm-400)";
  const dim = size === "sm" ? 6 : 8;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-sans)", ...style }}>
      <span style={{ display: "inline-flex", gap: 3 }}>
        {Array.from({ length: segments }).map((_, i) => (
          <span
            key={i}
            style={{
              width: dim,
              height: dim,
              borderRadius: "50%",
              background: i < filled ? tone : "var(--warm-200)",
            }}
          />
        ))}
      </span>
      {showLabel && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-body-sm)",
            fontWeight: 600,
            color: "var(--text-secondary)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {Math.round(pct * 100)}%
        </span>
      )}
    </span>
  );
}
